<script>
  import { onMount } from 'svelte';
  import SettingsLayout from '../../components/SettingsLayout.svelte';

  // Buildzilla's own QuickBooks connection. This is separate from JobTread's
  // QBO integration and is used for one thing: pushing overhead bills (no job)
  // straight to QuickBooks. Job bills still route through JobTread.

  let loading = true;
  let saving = false;
  let error = null;
  let success = null;

  let status = { connected: false, configured: false, sandbox: true, mock: false, manageable: true, companyName: null };
  let settings = { defaultOverheadAccountId: '', overheadAccountMappings: {}, taxCodeId: '', apAccountId: '', paymentAccountId: '' };

  let accounts = [];
  let payableAccounts = [];
  let paymentAccounts = [];
  let taxCodes = [];
  let vendors = [];
  let referenceLoaded = false;
  let referenceError = null;

  // Draft row for adding a per-vendor mapping.
  let newMappingVendorId = '';
  let newMappingAccountId = '';

  function flash(msg) {
    success = msg;
    setTimeout(() => { success = null; }, 2500);
  }

  function vendorLabel(key) {
    const hit = vendors.find((v) => String(v.id) === String(key));
    if (hit) return hit.displayName;
    // Name-keyed mappings survive a vendor being re-created in QBO.
    return key;
  }

  function accountLabel(id) {
    const hit = accounts.find((a) => String(a.id) === String(id));
    return hit ? hit.name : id;
  }

  async function loadStatus() {
    const res = await fetch('/api/qbo/status');
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.error || 'Could not read the QuickBooks connection status');
    status = data;
    settings = { ...settings, ...(data.overheadSettings || {}) };
  }

  async function loadReference() {
    if (!status.connected) return;
    referenceError = null;
    try {
      const [accRes, taxRes, venRes] = await Promise.all([
        fetch('/api/qbo/accounts'),
        fetch('/api/qbo/tax-codes'),
        fetch('/api/qbo/vendors')
      ]);
      const [acc, tax, ven] = await Promise.all([accRes.json(), taxRes.json(), venRes.json()]);
      if (acc.success) {
        accounts = acc.accounts || [];
        payableAccounts = acc.payableAccounts || [];
        paymentAccounts = acc.paymentAccounts || [];
      }
      if (tax.success) taxCodes = tax.taxCodes || [];
      if (ven.success) vendors = ven.vendors || [];
      referenceLoaded = true;
    } catch (err) {
      referenceError = err.message || 'Could not load QuickBooks accounts';
    }
  }

  onMount(async () => {
    try {
      // The OAuth callback bounces back here with a result in the query string.
      const params = new URLSearchParams(window.location.search);
      const outcome = params.get('qbo');
      if (outcome === 'connected') flash('✅ QuickBooks connected');
      if (outcome === 'declined') error = 'QuickBooks connection was declined.';
      if (outcome === 'error') error = 'QuickBooks connection failed. Try again.';
      if (outcome === 'mock-blocked') error = 'This server is running in QuickBooks mock mode (QBO_MOCK=1) and refused to replace your real connection. Restart without QBO_MOCK to connect for real.';

      await loadStatus();
      await loadReference();
    } catch (err) {
      error = err.message;
    } finally {
      loading = false;
    }
  });

  function connect() {
    window.location.href = '/api/qbo/login';
  }

  async function disconnect() {
    if (!confirm('Disconnect QuickBooks? Overhead bills will no longer sync until you reconnect.')) return;
    saving = true;
    error = null;
    try {
      const res = await fetch('/api/qbo/disconnect', { method: 'POST' });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Disconnect failed');
      accounts = [];
      paymentAccounts = [];
      taxCodes = [];
      vendors = [];
      referenceLoaded = false;
      await loadStatus();
      flash('QuickBooks disconnected');
    } catch (err) {
      error = err.message;
    } finally {
      saving = false;
    }
  }

  async function saveSettings() {
    saving = true;
    error = null;
    try {
      const res = await fetch('/api/qbo/overhead-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Could not save settings');
      settings = { ...settings, ...data.settings };
      flash('✅ Overhead settings saved');
    } catch (err) {
      error = err.message;
    } finally {
      saving = false;
    }
  }

  function addMapping() {
    if (!newMappingVendorId || !newMappingAccountId) return;
    settings.overheadAccountMappings = {
      ...settings.overheadAccountMappings,
      [newMappingVendorId]: newMappingAccountId
    };
    newMappingVendorId = '';
    newMappingAccountId = '';
  }

  function removeMapping(key) {
    const next = { ...settings.overheadAccountMappings };
    delete next[key];
    settings.overheadAccountMappings = next;
  }

  $: mappingEntries = Object.entries(settings.overheadAccountMappings || {});
</script>

<SettingsLayout activeTab="quickbooks" tabTitle="QuickBooks">
  {#if success}
    <div class="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-800">{success}</div>
  {/if}
  {#if error}
    <div class="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">{error}</div>
  {/if}

  {#if loading}
    <p class="text-sm text-gray-500">Loading QuickBooks connection…</p>
  {:else}
    <!-- Connection -->
    <div class="p-4 bg-gray-50 border border-gray-200 rounded-lg mb-4">
      <div class="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p class="text-sm font-semibold text-gray-900 mb-1">
            QuickBooks connection
            {#if status.mock}
              <span class="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-800">Mock</span>
            {:else if status.sandbox}
              <span class="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800">Sandbox</span>
            {/if}
          </p>
          <p class="text-xs text-gray-500 max-w-2xl">
            Buildzilla connects to QuickBooks separately from JobTread. This connection is used
            <strong>only</strong> for overhead bills — bills with no job. Job bills still go to JobTread,
            which syncs them to QuickBooks itself, so nothing is ever posted twice.
          </p>
        </div>
        <div class="shrink-0">
          {#if status.connected}
            <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <span class="w-1.5 h-1.5 rounded-full bg-green-500"></span> Connected
            </span>
          {:else}
            <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
              <span class="w-1.5 h-1.5 rounded-full bg-gray-400"></span> Not connected
            </span>
          {/if}
        </div>
      </div>

      {#if status.connected}
        <p class="mt-3 text-xs text-gray-600">
          Company: <strong>{status.companyName || 'QuickBooks company'}</strong>
        </p>
      {/if}

      {#if !status.configured}
        <p class="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
          QuickBooks isn't configured on this server yet — <code>QBO_CLIENT_ID</code>, <code>QBO_CLIENT_SECRET</code>
          and <code>QBO_REDIRECT_URI</code> need setting.
        </p>
      {/if}

      {#if status.manageable}
        <div class="mt-3 flex gap-2">
          {#if status.connected}
            <button type="button" on:click={disconnect} disabled={saving}
              class="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              Disconnect
            </button>
            <button type="button" on:click={connect} disabled={!status.configured}
              class="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              Reconnect
            </button>
          {:else}
            <button type="button" on:click={connect} disabled={!status.configured}
              class="px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
              Connect QuickBooks
            </button>
          {/if}
        </div>
      {:else}
        <p class="mt-3 text-xs text-gray-500">The QuickBooks connection is managed by your organisation owner.</p>
      {/if}
    </div>

    {#if status.connected}
      {#if referenceError}
        <div class="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">{referenceError}</div>
      {/if}

      <!-- Overhead coding -->
      <div class="p-4 bg-gray-50 border border-gray-200 rounded-lg mb-4">
        <p class="text-sm font-semibold text-gray-900 mb-1">Overhead coding</p>
        <p class="text-xs text-gray-500 mb-3">
          An overhead bill has no job and no cost code, so every line needs an expense account chosen another way.
          Buildzilla looks for a vendor mapping first, then falls back to the default account below.
          Without a default, overhead bills are blocked rather than posted uncoded.
        </p>

        <div class="grid gap-3 sm:grid-cols-2">
          <div>
            <label for="qbo-default-account" class="block text-xs font-medium text-gray-700 mb-1">Default overhead account</label>
            <select id="qbo-default-account" bind:value={settings.defaultOverheadAccountId} disabled={!status.manageable}
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100">
              <option value="">— Not set —</option>
              {#each accounts as account}
                <option value={account.id}>{account.name}</option>
              {/each}
            </select>
          </div>

          <div>
            <label for="qbo-tax-code" class="block text-xs font-medium text-gray-700 mb-1">Purchase tax code</label>
            <select id="qbo-tax-code" bind:value={settings.taxCodeId} disabled={!status.manageable}
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100">
              <option value="">— None —</option>
              {#each taxCodes as code}
                <option value={code.id}>{code.name}</option>
              {/each}
            </select>
          </div>

          <div>
            <label for="qbo-ap-account" class="block text-xs font-medium text-gray-700 mb-1">
              Accounts Payable account <span class="font-normal text-gray-400">(optional)</span>
            </label>
            <select id="qbo-ap-account" bind:value={settings.apAccountId} disabled={!status.manageable}
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100">
              <option value="">— Let QuickBooks decide —</option>
              {#each payableAccounts as account}
                <option value={account.id}>{account.name}</option>
              {/each}
            </select>
          </div>
        </div>

        {#if !settings.defaultOverheadAccountId && mappingEntries.length === 0}
          <p class="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            Set a default overhead account before sending overhead to QuickBooks.
          </p>
        {/if}
      </div>

      <!-- Already-paid overheads -->
      <div class="p-4 bg-gray-50 border border-gray-200 rounded-lg mb-4">
        <p class="text-sm font-semibold text-gray-900 mb-1">Already-paid overheads</p>
        <p class="text-xs text-gray-500 mb-3">
          A receipt that's already been paid — fuel on a card, a subscription on direct debit —
          is posted to QuickBooks as an <strong>Expense</strong> against the account it came out of,
          not as a bill. Booking those as bills leaves payables sitting in QuickBooks for
          someone to clear by hand. A credit-card account posts as a credit-card expense.
          A bank account posts as cash.
        </p>

        <div>
          <label for="qbo-payment-account" class="block text-xs font-medium text-gray-700 mb-1">Paid from</label>
          <select id="qbo-payment-account" bind:value={settings.paymentAccountId} disabled={!status.manageable}
            class="w-full max-w-md px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100">
            <option value="">— Not set —</option>
            {#each paymentAccounts as account}
              <option value={account.id}>{account.name} ({account.accountType})</option>
            {/each}
          </select>
        </div>

        {#if !settings.paymentAccountId}
          <p class="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            Without this, a bill marked "already paid" can't be sent — it would have to go to
            Accounts Payable instead, which is the wrong books.
          </p>
        {/if}
      </div>

      <!-- Per-vendor mappings -->
      <div class="p-4 bg-gray-50 border border-gray-200 rounded-lg mb-4">
        <p class="text-sm font-semibold text-gray-900 mb-1">Vendor account mappings</p>
        <p class="text-xs text-gray-500 mb-3">
          Map a supplier to the account its overheads always land in — fuel cards to Motor Vehicle Expenses, for example.
          A mapped vendor is coded automatically.
        </p>

        {#if mappingEntries.length > 0}
          <div class="mb-3 divide-y divide-gray-200 border border-gray-200 rounded-md bg-white">
            {#each mappingEntries as [vendorKey, accountId]}
              <div class="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <span class="font-medium text-gray-800 truncate">{vendorLabel(vendorKey)}</span>
                <span class="text-gray-400">&rarr;</span>
                <span class="text-gray-700 truncate flex-1">{accountLabel(accountId)}</span>
                {#if status.manageable}
                  <button type="button" on:click={() => removeMapping(vendorKey)}
                    class="text-xs text-red-600 hover:text-red-800 shrink-0">Remove</button>
                {/if}
              </div>
            {/each}
          </div>
        {:else}
          <p class="mb-3 text-xs text-gray-400 italic">No vendor mappings yet.</p>
        {/if}

        {#if status.manageable && referenceLoaded}
          <div class="flex flex-wrap items-end gap-2">
            <div class="flex-1 min-w-[180px]">
              <label for="qbo-map-vendor" class="block text-xs font-medium text-gray-700 mb-1">Vendor</label>
              <select id="qbo-map-vendor" bind:value={newMappingVendorId}
                class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500">
                <option value="">— Select —</option>
                {#each vendors as vendor}
                  <option value={vendor.id}>{vendor.displayName}</option>
                {/each}
              </select>
            </div>
            <div class="flex-1 min-w-[180px]">
              <label for="qbo-map-account" class="block text-xs font-medium text-gray-700 mb-1">Expense account</label>
              <select id="qbo-map-account" bind:value={newMappingAccountId}
                class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500">
                <option value="">— Select —</option>
                {#each accounts as account}
                  <option value={account.id}>{account.name}</option>
                {/each}
              </select>
            </div>
            <button type="button" on:click={addMapping} disabled={!newMappingVendorId || !newMappingAccountId}
              class="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              Add
            </button>
          </div>
        {/if}
      </div>

      {#if status.manageable}
        <button type="button" on:click={saveSettings} disabled={saving}
          class="px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
          {saving ? 'Saving…' : 'Save overhead settings'}
        </button>
      {/if}
    {/if}
  {/if}
</SettingsLayout>
